/**
 * InvoiceAiCard — org opt-in for the Claude-powered PO invoice analyzer. The
 * server must also carry ANTHROPIC_API_KEY for the feature to be live; when the
 * org has opted in but the key is missing, an env-gated notice explains the
 * "on but unavailable" state instead of silently snapping the switch off.
 *
 * Lives on the Xero workspace Overview right rail.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';

interface InvoiceAiCardProps {
  /** updateAiSettings is an adminProcedure — non-admins view read-only. */
  isAdmin: boolean;
}

export function InvoiceAiCard({ isAdmin }: InvoiceAiCardProps) {
  const utils = trpc.useUtils();
  const aiStatusQuery = trpc.poInvoices.aiStatus.useQuery();
  // aiStatus.enabled is the EFFECTIVE state (org opt-in AND server API key).
  // Track the org's saved opt-in locally so an env-gated "on but unavailable"
  // state can be explained instead of silently snapping the switch back off.
  const [savedOptIn, setSavedOptIn] = useState<boolean | null>(null);

  const updateAiSettings = trpc.poInvoices.updateAiSettings.useMutation({
    onSuccess: async (result) => {
      setSavedOptIn(result.invoiceAiEnabled);
      await utils.poInvoices.aiStatus.invalidate();
      toast.success(
        result.invoiceAiEnabled ? 'AI invoice ingestion enabled' : 'AI invoice ingestion disabled'
      );
    },
    onError: (error) => toast.error(error.message || 'Failed to update AI settings'),
  });

  const effectiveEnabled = aiStatusQuery.data?.enabled ?? false;
  const switchChecked = savedOptIn ?? effectiveEnabled;
  const envGated = savedOptIn === true && !effectiveEnabled && !aiStatusQuery.isLoading;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Sparkles className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div>
              <CardTitle className="text-base">AI invoice ingestion</CardTitle>
              <CardDescription>
                Attach a PDF invoice on a purchase order and the IMS pre-fills the invoice form
                from it. Proposals are never saved automatically, you always review first.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground" id="invoice-ai-enabled-label">
              {switchChecked ? 'On' : 'Off'}
            </span>
            <Switch
              checked={switchChecked}
              onCheckedChange={(next) => updateAiSettings.mutate({ invoiceAiEnabled: next })}
              disabled={aiStatusQuery.isLoading || updateAiSettings.isPending || !isAdmin}
              aria-labelledby="invoice-ai-enabled-label"
              title={isAdmin ? undefined : 'Changing AI invoice ingestion requires Admin access'}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {envGated && (
          <Alert variant="warning">
            <AlertDescription>
              The org opt-in is saved, but analysis stays unavailable until{' '}
              <code>ANTHROPIC_API_KEY</code> is configured on the server.
            </AlertDescription>
          </Alert>
        )}
        <p className="text-xs text-muted-foreground">
          Requires <code>ANTHROPIC_API_KEY</code> on the backend. Extraction runs on the uploaded
          PDF only, flags variances against the PO, and suggests account codes from your
          dropdown-visible chart of accounts.
        </p>
      </CardContent>
    </Card>
  );
}
